import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { SubstanceTypeEnum } from 'app/enums/substance-type-enum';
import { FormService } from 'app/services/form.service';

@Component({
  selector: 'app-molecular-weight',
  templateUrl: './molecular-weight.component.html',
  styleUrls: ['./molecular-weight.component.scss']
})


export class MolecularWeightComponent implements OnInit {
  @Input({required: true}) substanceType!: SubstanceTypeEnum;
  @Input() shouldShowFullTitle!: boolean; // Only if single entity/salt shouldn't be displayed on their respective pages.
  molecularWeight!: string;

  constructor(private formService: FormService) {}

  ngOnInit(): void {
    this.molecularWeight = this.currentFormValue;
  }

  public clearMolecularWeightInputField() {
    this.molecularWeight = '';
    this.patchValueToCurrentForm('');
    this.errorCheck();
  }

  public updateMolecularWeight(event: Event) {
    if (event && event.type === 'blur') {
      var target = document.activeElement;
      if (target && target.nodeValue === "cancel") {
        this.molecularWeight = '';
      }
    }
    this.patchValueToCurrentForm(this.molecularWeight);
    this.errorCheck();
  }

  public cancelUpdate() {
    this.molecularWeight = this.currentFormValue;
    this.errorCheck();
  }

  get currentFormValue() {
    if(this.isNoneWeightInputType || this.isBiologicsInputType) {
      return this.currentForm.get('molecularWeight')?.value;
    }

    if(this.isSingleEntityWeightInputType) {
      return this.currentForm.get('molecularWeightForSingleEntity')?.value; 
    }
    if(this.isSaltWeightInputType) {
      return this.currentForm.get('molecularWeightForSaltForm')?.value; 
    }
    
    return '';
  }

  private patchValueToCurrentForm(value: string) {
    this.currentForm.patchValue({ [this.formFieldName] : value });
  }

  get isNoneWeightInputType() {
    return this.substanceType === SubstanceTypeEnum.None;
  }

  get isSingleEntityWeightInputType() {
    return this.substanceType === SubstanceTypeEnum.SingleEntity;
  }

  get isSaltWeightInputType() {
    return this.substanceType === SubstanceTypeEnum.Salt;
  }

  get isBiologicsInputType() {
    return this.substanceType === SubstanceTypeEnum.Biologics;
  }

  get formFieldName() {
    if(this.isNoneWeightInputType || this.isBiologicsInputType) {
      return 'molecularWeight';
    }

    if(this.isSingleEntityWeightInputType) {
      return 'molecularWeightForSingleEntity'; 
    }
    if(this.isSaltWeightInputType) {
      return 'molecularWeightForSaltForm'; 
    }

    return '';
  }

  get formFieldTitle() {
    var title = 'Molecular weight';

    if(this.isSingleEntityWeightInputType && this.shouldShowFullTitle) {
      title += ' for single entity'; 
    }
    if(this.isSaltWeightInputType && this.shouldShowFullTitle) {
      title += ' for salt form'; 
    }

    return title;
  }

  get infoText() {
    if(this.isFormAType || this.isFormBType || this.isFormCType) {
      return `Please indicate the unit of measurement and whether this is theoretical/calculated or measured. 
        If measured, please indicate the type of measurement(s) used. If calculated, please indicate the type(s) 
        of calculations used.`;
    }

    if(this.isFormDType) {
      return `Enter numerical value and indicate the unit of measurement.`;
    }

    if(this.isFormEType) {
      return `Please indicate the unit of measurement and whether this is theoretical/calculated or measured. If 
        measured, please indicate the type of measurement(s) used. If calculated, please indicate the type(s) of 
        calculations used. For polymers or substances with a variable weight, list a molecular weight range or 
        average molecular weight with standard deviations.`;
    }

    if(this.isFormFType) {
      return `Please indicate the unit of measurement and whether this is theoretical/calculated or measured. 
        If measured, please indicate the type of measurement(s) used. If calculated, please indicate the 
        type(s) of calculations used. Not required for cell therapies.`;
    }

    return ``;
  }

  private errorCheck() {
    const targetControl = this.currentForm.get(this.formFieldName);
    if(!targetControl) {
      return;
    }

    // Force re-validation
    setTimeout(() => {
      targetControl.markAsTouched();
      targetControl.markAsDirty();
      targetControl.updateValueAndValidity();
    });
  }

  get currentForm(): FormGroup {
    return this.formService.getCurrentUsanFormValue();
  }

  public hasFieldValidationError(fieldName: string, errorName?: string): boolean {
    return this.formService.hasFieldValidationError(fieldName, errorName);
  }

  get isFormAType(): boolean {
    return 'FormA' === this.formType;
  }

  get isFormBType(): boolean {
    return 'FormB' === this.formType;
  }

  get isFormCType(): boolean {
    return 'FormC' === this.formType;
  }

  get isFormDType(): boolean {
    return 'FormD' === this.formType;
  }

  get isFormEType(): boolean {
    return 'FormE' === this.formType;
  }

  get isFormFType(): boolean {
    return 'FormF' === this.formType;
  }
  
  get formType(): string {
    return this.formService.getCurrentUsanFormType();
  }
}
